import { Controller } from "@hotwired/stimulus"
import { post } from "@lib/requests"
import { showAlert } from "@lib/utils"

// Connects to data-controller="file-drop"
export default class extends Controller {

  static values = {
    associatedRecords: String
  }

  connect() {
    this.bindEvents()
  }

  bindEvents() {
    const zone = document.body
    zone.addEventListener("dragenter", this.dragEnter.bind(this))
    zone.addEventListener("dragover", this.dragOver.bind(this))
    zone.addEventListener("drop", this.drop.bind(this))
    zone.addEventListener("dragleave", this.dragLeave.bind(this))
  }

  get visibleModalPresent() {
    return Array.from(document.querySelectorAll(".modal")).some(modal => modal.checkVisibility())
  }

  dragEnter(event) {
    event.preventDefault()
    if (this.visibleModalPresent) return // Don't use file drop when a modal is visible
    if (event.relatedTarget) return // relatedTarget is null when we are entering the page

    // Only activate for file transfers from outside the page
    if (!this.hasFileItems(event.dataTransfer)) return

    document.getElementById("file-drag-layer").classList.add("active")
  }

  // Helper method to check if the dataTransfer contains file items
  hasFileItems(dataTransfer) {
    if (!dataTransfer || !dataTransfer.types) return false

    // Check if files are being dragged (either from a file type or Files type entry)
    return dataTransfer.types.includes('Files') || dataTransfer.types.includes('application/x-moz-file')
  }

  dragOver(event) {
    event.preventDefault()
  }

  drop(event) {
    event.preventDefault()

    if (this.visibleModalPresent) return // Don't use file drop when a modal is visible

    // If the drop doesn't contain files, ignore it
    if (!this.hasFileItems(event.dataTransfer)) {
      this.hideLayer()
      return
    }

    // Check for folders
    const items = event.dataTransfer.items
    let folderCount = 0

    for (const item of items) {
      // Use webkitGetAsEntry to get the entry
      const entry = item.webkitGetAsEntry()
      if (entry && entry.isDirectory) folderCount++
    }

    if (folderCount) {
      showAlert("Vous ne pouvez pas télécharger un dossier. Essayez plutot de faire glisser tous les fichiers du dossier en même temps.", { type: "warning" })
      this.hideLayer()
      return
    }

    const files = event.dataTransfer.files

    if (!files.length) {
      this.hideLayer()
      return
    }

    // Should we check here for total size and throw an error if it's too big?
    const totalSize = Array.from(files).reduce((acc, file) => acc + file.size, 0)

    // If total size is bigger than 50MB, show an error
    if (totalSize > 50 * 1024 * 1024) {
      showAlert("Le total des fichiers ne doit pas dépasser 50MB.", { type: "warning" })
      this.hideLayer()
      return
    }

    const formData = new FormData()

    formData.append("resources", this.associatedRecordsValue)

    Array.from(files).forEach(file => {
      formData.append('files[]', file)
    })

    post('/fichiers/upload', {
      body: formData,
      responseKind: 'turbo-stream'
    })

    // The server will replace the file-drag-layer element so we don't need to hide it here
    // In the meantime, we will update the div text
    const div = document.getElementById("file-drag-layer").querySelector("div")
    div.textContent = `Traitement de ${files.length} fichier${files.length > 1 ? 's' : ''} en cours...`

  }

  dragLeave(event) {
    event.preventDefault()
    if (this.visibleModalPresent) return // Don't use file drop when a modal is visible
    if (event.relatedTarget) return // relatedTarget is null when we are leaving the page
    document.getElementById("file-drag-layer").classList.remove("active")
  }

  hideLayer() {
    document.getElementById("file-drag-layer").classList.remove("active")
  }
}
